import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface EditContactDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialData: {
        contact_first_name: string;
        contact_last_name: string;
        phone?: string;
        email?: string;
    };
    onSave?: (data: { contact_first_name: string; contact_last_name: string; phone?: string; email?: string }) => void;
}

export function EditContactDialog({ isOpen, onClose, initialData, onSave }: EditContactDialogProps) {
    const { t } = useTranslations();
    const [formData, setFormData] = useState(initialData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contactError, setContactError] = useState<string | null>(null);

    /**
     * A work-order contact has to be reachable, but either channel will do — some
     * sites give a phone and no address, others the reverse. So neither field is
     * required on its own; what is required is that at least one is filled.
     */
    const hasContactChannel = Boolean(formData.phone?.trim() || formData.email?.trim());

    useEffect(() => {
        if (isOpen) {
            setFormData(initialData);
            setContactError(null);
        }
    }, [isOpen, initialData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!hasContactChannel) {
            setContactError(t('work_orders.forms.edit_contact.validation.phone_or_email', 'Enter a phone number or an email address.'));
            return;
        }

        setContactError(null);
        setIsSubmitting(true);

        try {
            await onSave?.(formData);
            // Don't close here - let the parent handle closing after successful save
        } catch (error) {
            console.error('Failed to save contact:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(initialData);
            setContactError(null);
            onClose();
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="lg">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('work_orders.forms.edit_contact.title', 'Edit Contact Details')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('work_orders.forms.edit_contact.description', 'Update the contact information for this work order.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="contact_first_name">{t('work_orders.forms.edit_contact.labels.first_name', 'First Name')} *</Label>
                                <Input
                                    id="contact_first_name"
                                    value={formData.contact_first_name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, contact_first_name: e.target.value }))}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_last_name">{t('work_orders.forms.edit_contact.labels.last_name', 'Last Name')} *</Label>
                                <Input
                                    id="contact_last_name"
                                    value={formData.contact_last_name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, contact_last_name: e.target.value }))}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="phone">{t('work_orders.forms.edit_contact.labels.phone', 'Phone Number')}</Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        value={formData.phone || ''}
                                        onChange={(e) => {
                                            setContactError(null);
                                            setFormData((prev) => ({ ...prev, phone: e.target.value }));
                                        }}
                                        placeholder={t('work_orders.forms.edit_contact.placeholders.phone', '+1 (555) 123-4567')}
                                        aria-invalid={contactError !== null}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('work_orders.forms.edit_contact.labels.email', 'Email Address')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email || ''}
                                        onChange={(e) => {
                                            setContactError(null);
                                            setFormData((prev) => ({ ...prev, email: e.target.value }));
                                        }}
                                        placeholder={t('work_orders.forms.edit_contact.placeholders.email', 'contact@example.com')}
                                        aria-invalid={contactError !== null}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                            {/* One message under the pair, not one per field: the rule is
                                about the two together, so repeating it twice would read
                                as two separate problems. */}
                            <p className={contactError ? 'text-destructive text-sm' : 'text-muted-foreground text-xs'}>
                                {contactError ?? t('work_orders.forms.edit_contact.hints.phone_or_email', 'Enter at least one of phone or email.')}
                            </p>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            {t('work_orders.forms.edit_contact.buttons.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('work_orders.forms.edit_contact.buttons.saving', 'Saving...')}
                                </>
                            ) : (
                                t('work_orders.forms.edit_contact.buttons.save', 'Save Changes')
                            )}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
